import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { InjectAppServices } from '../../../services/pure-di';
import { getPlanTypeFromUrlSegment } from '../../../utils';
import { PaymentFrequency } from '../PaymentFrequency';
import { ItemCart } from './ItemCart';
import { usePaymentMethodData } from '../../../hooks/usePaymentMethodData';
import {
  getBuyButton,
  mapItemFromLandingPackages,
  mapItemFromMarketingPlan,
  mapItemFromAddOnPlan,
} from './utils';
import {
  AddOnType,
  BUY_ECO_IA_PLAN,
  BUY_CHAT_PLAN,
  BUY_LANDING_PACK,
  BUY_MARKETING_PLAN,
  BUY_ONSITE_PLAN,
  BUY_PUSH_NOTIFICATION_PLAN,
  PLAN_TYPE,
  PaymentMethodType,
  SUBSCRIBERS_LIMIT_EXCLUSIVE_DISCOUNT_ARGENTINA,
  URL_PLAN_TYPE,
} from '../../../doppler-types';
import { Promocode } from './Promocode';
import { NextInvoices } from './NextInvoices';
import { useQueryParams } from '../../../hooks/useQueryParams';

const isArgentinaTransfer = (billingCountry, selectedPaymentMethod) =>
  billingCountry?.toLowerCase() === 'ar' && selectedPaymentMethod === PaymentMethodType.transfer;

const buildAmountDetailsRequestKey = ({
  selectedMarketingPlanId,
  paymentFrequencyId,
  effectivePromocode,
  paymentMethodName,
}) =>
  [selectedMarketingPlanId ?? '', paymentFrequencyId, effectivePromocode, paymentMethodName].join(
    '|',
  );

const numberFormatOptions = {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: true,
};

const getEmptyPromocodeState = () => ({
  canApply: false,
  promocode: '',
});

const normalizePromocodeState = (promotion) => {
  if (!promotion || typeof promotion !== 'object') {
    return getEmptyPromocodeState();
  }

  return {
    ...getEmptyPromocodeState(),
    ...promotion,
    canApply: Boolean(promotion.canApply),
    promocode: promotion.promocode ?? '',
  };
};

export const ShoppingCart = InjectAppServices(
  ({
    discountConfig,
    selectedMarketingPlan,
    landingPacks,
    isEqualPlan = true,
    canBuy = true,
    selectedPaymentMethod,
    isArgentina,
    hidePromocode = false,
    buyType = BUY_MARKETING_PLAN,
    handleLandingPagesDowngrade,
    disabledLandingsBuy,
    landingPagesRemoved,
    cancelLandings,
    hasChatActive,
    disabledPromocode = false,
    addMarketingPlan = true,
    selectedAddOnPlan,
    handleRemoveAddOnPlan,
    canAddOnPlanRemove,
    canAddOnPlanContinueBuy = true,
    callbackHandlePromocodeApplied,
    billingCountry,
    dependencies: { appSessionRef, dopplerAccountPlansApiClient, dopplerBillingUserApiClient },
  }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const query = useQueryParams();
    const promocodeFromUrl = query.get('promo-code') ?? query.get('PromoCode') ?? '';

    const [amountDetailsData, setAmountDetailsData] = useState(null);
    const [amountDetailsLandingPacksData, setAmountDetailsLandingPacksData] = useState(null);
    const [amountDetailsAddOnPlanData, setAmountDetailsAddOnPlanData] = useState(null);
    const [promocodeApplied, setPromocodeApplied] = useState(() =>
      normalizePromocodeState(
        promocodeFromUrl ? { canApply: true, promocode: promocodeFromUrl } : null,
      ),
    );
    const [defaultPromocodeDismissed, setDefaultPromocodeDismissed] = useState(false);
    const clearPromocodeInputRef = useRef(null);
    const lastAmountDetailsRequestRef = useRef({
      key: '',
      id: 0,
    });
    const { planType: planTypeUrlSegment } = useParams();
    const { pathname, search } = useLocation();
    const paymentMethodName = usePaymentMethodData({
      dopplerBillingUserApiClient,
      selectedPaymentMethod,
    });
    const selectedPlanType = useMemo(
      () => getPlanTypeFromUrlSegment(planTypeUrlSegment),
      [planTypeUrlSegment],
    );
    const sessionPlan = appSessionRef.current.userData.user;
    const { isFreeAccount } = sessionPlan.plan;
    const sessionPlanType = sessionPlan.plan.planType;
    const isExclusiveDiscountArgentina =
      isArgentina &&
      isFreeAccount &&
      selectedMarketingPlan?.type === PLAN_TYPE.byContact &&
      (selectedMarketingPlan?.subscriberLimit <= SUBSCRIBERS_LIMIT_EXCLUSIVE_DISCOUNT_ARGENTINA ||
        selectedMarketingPlan?.subscribersQty <= SUBSCRIBERS_LIMIT_EXCLUSIVE_DISCOUNT_ARGENTINA);
    const amountDetailsLandingPacksDataRef = useRef(null);
    const initialAmountDetailsLandingPacksDataRef = useRef(null);
    amountDetailsLandingPacksDataRef.current = amountDetailsLandingPacksData;

    const isByCredits =
      planTypeUrlSegment === PLAN_TYPE.byCredit ||
      planTypeUrlSegment === URL_PLAN_TYPE[PLAN_TYPE.byCredit];
    const paymentFrequencyId =
      discountConfig?.selectedPaymentFrequency?.id ??
      discountConfig?.paymentFrequenciesList?.at(-1)?.id ??
      0;

    const effectivePromocode = promocodeApplied?.canApply
      ? promocodeApplied.promocode
      : !defaultPromocodeDismissed
        ? promocodeFromUrl
        : '';
    const resolvedPaymentMethod = paymentMethodName ?? PaymentMethodType.creditCard;

    useEffect(() => {
      const currentRequestKey = buildAmountDetailsRequestKey({
        selectedMarketingPlanId: selectedMarketingPlan?.id,
        paymentFrequencyId,
        effectivePromocode,
        paymentMethodName: resolvedPaymentMethod,
      });

      if (!selectedMarketingPlan?.id || !paymentMethodName) {
        return;
      }

      if (lastAmountDetailsRequestRef.current.key === currentRequestKey) {
        return;
      }

      const requestId = lastAmountDetailsRequestRef.current.id + 1;
      lastAmountDetailsRequestRef.current = {
        key: currentRequestKey,
        id: requestId,
      };

      const fetchData = async () => {
        const _amountDetailsData = await dopplerAccountPlansApiClient.getPlanBillingDetailsData(
          selectedMarketingPlan?.id,
          'Marketing',
          paymentFrequencyId,
          effectivePromocode,
          resolvedPaymentMethod,
        );

        if (lastAmountDetailsRequestRef.current.id === requestId) {
          setAmountDetailsData(_amountDetailsData);
        }
      };

      fetchData();
    }, [
      dopplerAccountPlansApiClient,
      selectedMarketingPlan?.id,
      paymentFrequencyId,
      effectivePromocode,
      paymentMethodName,
      resolvedPaymentMethod,
    ]);

    useEffect(() => {
      const fetchData = async () => {
        const _amountDetailsLandingPacksData =
          await dopplerAccountPlansApiClient.getPlanBillingDetailsLandingPacksData(
            landingPacks.map((item) => item.planId).toString(),
            landingPacks.map((item) => item.packagesQty).toString(),
            selectedPaymentMethod,
          );

        setAmountDetailsLandingPacksData(_amountDetailsLandingPacksData);
        if (!initialAmountDetailsLandingPacksDataRef.current) {
          initialAmountDetailsLandingPacksDataRef.current = _amountDetailsLandingPacksData;
        }
      };

      if (landingPacks?.length > 0) {
        fetchData();
      } else if (amountDetailsLandingPacksDataRef.current) {
        setAmountDetailsLandingPacksData(null);
      }
    }, [
      dopplerAccountPlansApiClient,
      discountConfig?.selectedPaymentFrequency,
      discountConfig.paymentFrequenciesList,
      landingPacks,
      selectedPaymentMethod,
    ]);

    useEffect(() => {
      const total = amountDetailsLandingPacksData?.value?.currentMonthTotal;
      if (buyType === BUY_LANDING_PACK) {
        handleLandingPagesDowngrade &&
          handleLandingPagesDowngrade(
            total === 0 && amountDetailsLandingPacksData?.value?.positiveBalance < 0,
          );
      }
    }, [amountDetailsLandingPacksData, buyType, handleLandingPagesDowngrade]);

    useEffect(() => {
      const fetchData = async () => {
        const paymentFrequencyId = discountConfig?.selectedPaymentFrequency?.id;
        const _amountDetailsAddOnPlanData =
          await dopplerAccountPlansApiClient.getAddOnPlanBillingDetailsData(
            selectedAddOnPlan?.planId,
            buyType === BUY_ONSITE_PLAN
              ? 4
              : buyType === BUY_PUSH_NOTIFICATION_PLAN
                ? 5
                : buyType === BUY_CHAT_PLAN
                  ? 2
                  : 6,
            paymentFrequencyId
              ? paymentFrequencyId
              : discountConfig.paymentFrequenciesList.at(-1)
                ? discountConfig.paymentFrequenciesList.at(-1).id
                : 0,
            selectedPaymentMethod,
          );

        setAmountDetailsAddOnPlanData(_amountDetailsAddOnPlanData);
      };

      if (selectedAddOnPlan?.planId) {
        fetchData();
      } else {
        setAmountDetailsAddOnPlanData(null);
      }
    }, [
      dopplerAccountPlansApiClient,
      selectedAddOnPlan,
      buyType,
      discountConfig?.selectedPaymentFrequency,
      discountConfig.paymentFrequenciesList,
      selectedPaymentMethod,
    ]);

    const handlePromocodeApplied = useCallback(
      (value) => {
        const normalizedPromocode = normalizePromocodeState(value);
        setPromocodeApplied(normalizedPromocode);

        if (callbackHandlePromocodeApplied) {
          callbackHandlePromocodeApplied(
            normalizedPromocode.promocode ? normalizedPromocode : null,
          );
        }
      },
      [callbackHandlePromocodeApplied],
    );

    const removePromocodeApplied = () => {
      clearPromocodeInputRef.current?.();
      setDefaultPromocodeDismissed(true);
      setPromocodeApplied(getEmptyPromocodeState());

      if (callbackHandlePromocodeApplied) {
        callbackHandlePromocodeApplied(null);
      }

      setAmountDetailsData((currentAmountDetailsData) =>
        currentAmountDetailsData
          ? {
              ...currentAmountDetailsData,
              value: {
                ...currentAmountDetailsData?.value,
                discountPromocode: null,
              },
            }
          : currentAmountDetailsData,
      );
    };

    const handleManualPromocodeIntervention = useCallback(() => {
      setDefaultPromocodeDismissed(true);
    }, []);

    const registerClearPromocodeInput = useCallback((clearPromocodeInput) => {
      clearPromocodeInputRef.current = clearPromocodeInput;
    }, []);

    const items = [];
    const isTransfer = paymentMethodName === PaymentMethodType.transfer;
    selectedMarketingPlan &&
      addMarketingPlan &&
      items.push(
        mapItemFromMarketingPlan({
          marketingPlan: selectedMarketingPlan,
          selectedPaymentFrequency: discountConfig?.selectedPaymentFrequency,
          amountDetailsData,
          promocodeApplied: promocodeApplied,
          removePromocodeApplied,
          disabledPromocode,
          intl,
          isExclusiveDiscountArgentina:
            isArgentina &&
            isFreeAccount &&
            selectedMarketingPlan?.type === PLAN_TYPE.byContact &&
            promocodeApplied?.promocode === process.env.REACT_APP_PROMOCODE_ARGENTINA,
          hidePromocode,
          selectedPaymentMethod,
          billingCountry,
        }),
      );

    landingPacks?.length > 0 &&
      items.push(
        mapItemFromLandingPackages({
          landingPacks,
          selectedPaymentFrequency: discountConfig?.selectedPaymentFrequency,
          amountDetailsData: amountDetailsLandingPacksData,
          sessionPlan: appSessionRef.current.userData.user.plan,
          intl,
          selectedPaymentMethod,
          billingCountry,
        }),
      );

    selectedAddOnPlan &&
      items.push(
        mapItemFromAddOnPlan({
          addOnPlan: selectedAddOnPlan,
          addOnType:
            buyType === BUY_ONSITE_PLAN
              ? AddOnType.OnSite
              : buyType === BUY_PUSH_NOTIFICATION_PLAN
                ? AddOnType.PushNotifications
                : buyType === BUY_CHAT_PLAN
                  ? AddOnType.Conversations
                  : AddOnType.EcoAI,
          intl,
          selectedPaymentFrequency: discountConfig?.selectedPaymentFrequency,
          amountDetailsData: amountDetailsAddOnPlanData,
          planType: sessionPlanType,
          handleRemove: () => {
            handleRemoveAddOnPlan();
          },
          canAddOnPlanRemove: canAddOnPlanRemove,
          selectedPaymentMethod,
          billingCountry,
        }),
      );

    const currencyRate =
      buyType === BUY_MARKETING_PLAN
        ? Number(amountDetailsData?.value?.currencyRate) || 0
        : buyType === BUY_LANDING_PACK
          ? Number(amountDetailsLandingPacksData?.value?.currencyRate) || 0
          : buyType === BUY_ONSITE_PLAN ||
              buyType === BUY_PUSH_NOTIFICATION_PLAN ||
              buyType === BUY_CHAT_PLAN ||
              buyType === BUY_ECO_IA_PLAN
            ? Number(amountDetailsAddOnPlanData?.value?.currencyRate) || 0
            : 0;

    const currentMonthTotal =
      buyType === BUY_MARKETING_PLAN
        ? Number(amountDetailsData?.value?.currentMonthTotal) || 0
        : buyType === BUY_LANDING_PACK
          ? Number(amountDetailsLandingPacksData?.value?.currentMonthTotal) || 0
          : buyType === BUY_ONSITE_PLAN ||
              buyType === BUY_PUSH_NOTIFICATION_PLAN ||
              buyType === BUY_CHAT_PLAN ||
              buyType === BUY_ECO_IA_PLAN
            ? Number(amountDetailsAddOnPlanData?.value?.currentMonthTotal) || 0
            : 0;

    const taxes =
      buyType === BUY_MARKETING_PLAN
        ? Number(amountDetailsData?.value?.taxes) || 0
        : buyType === BUY_LANDING_PACK
          ? Number(amountDetailsLandingPacksData?.value?.taxes) || 0
          : buyType === BUY_ONSITE_PLAN ||
              buyType === BUY_PUSH_NOTIFICATION_PLAN ||
              buyType === BUY_CHAT_PLAN ||
              buyType === BUY_ECO_IA_PLAN
            ? Number(amountDetailsAddOnPlanData?.value?.taxes) || 0
            : 0;

    const nextMonthTotal =
      buyType === BUY_MARKETING_PLAN
        ? Number(amountDetailsData?.value?.nextMonthTotal) || 0
        : buyType === BUY_LANDING_PACK
          ? Number(amountDetailsLandingPacksData?.value?.nextMonthTotal) || 0
          : buyType === BUY_ONSITE_PLAN ||
              buyType === BUY_PUSH_NOTIFICATION_PLAN ||
              buyType === BUY_CHAT_PLAN ||
              buyType === BUY_ECO_IA_PLAN
            ? Number(amountDetailsAddOnPlanData?.value?.nextMonthTotal) || 0
            : 0;

    const total = currentMonthTotal;
    const nextMonthTaxes =
      buyType === BUY_MARKETING_PLAN
        ? Number(amountDetailsData?.value?.nextMonthTaxes) || 0
        : buyType === BUY_LANDING_PACK
          ? Number(amountDetailsLandingPacksData?.value?.nextMonthTaxes) || 0
          : buyType === BUY_ONSITE_PLAN ||
              buyType === BUY_PUSH_NOTIFICATION_PLAN ||
              buyType === BUY_CHAT_PLAN ||
              buyType === BUY_ECO_IA_PLAN
            ? Number(amountDetailsAddOnPlanData?.value?.nextMonthTaxes) || 0
            : 0;

    const checkoutLandingPackButtonEnabled = landingPagesRemoved && landingPacks?.length === 0;
    const buyButton = getBuyButton({
      pathname,
      isEqualPlan,
      sessionPlanType,
      selectedMarketingPlan: {
        ...selectedMarketingPlan,
        total: (total + taxes) * currencyRate,
      },
      canBuy,
      selectedDiscount: discountConfig?.selectedPaymentFrequency,
      promotion: promocodeApplied || { promocode: promocodeFromUrl, canApply: true },
      paymentMethodName,
      total,
      landingPacks,
      buyType,
      disabledLandingsBuy,
      checkoutLandingPackButtonEnabled,
      cancelLandings,
      hasChatActive,
      selectedAddOnPlan: {
        addOnPlan: selectedAddOnPlan,
        total: (total + taxes) * currencyRate,
      },
      selectedLandingsPlan: {
        landingPacks: landingPacks,
        total: (total + taxes) * currencyRate,
      },
      canAddOnPlanContinueBuy,
    });

    const paymentFrequencyProps = {
      ...discountConfig,
      disabled: discountConfig.disabled,
      isExclusiveDiscountArgentina,
      promocodeApplied,
    };

    const isLandingPagesDowngrade =
      buyType === BUY_LANDING_PACK &&
      total === 0 &&
      amountDetailsLandingPacksData?.value?.positiveBalance < 0;

    return (
      <div className="dp-shopping-card">
        <header>
          <h3 className="dp-second-order-title">{_('buy_process.shopping_cart.title')}</h3>
        </header>

        <PaymentFrequency {...paymentFrequencyProps} />

        {buyType === BUY_MARKETING_PLAN && !hidePromocode && (
          <Promocode
            allowPromocode={
              !discountConfig?.selectedPaymentFrequency?.id ||
              discountConfig?.selectedPaymentFrequency?.applyPromo
            }
            selectedMarketingPlan={selectedMarketingPlan}
            amountDetailsData={amountDetailsData}
            selectedPaymentFrequency={discountConfig?.selectedPaymentFrequency}
            callback={handlePromocodeApplied}
            hasPromocodeAppliedItem={promocodeApplied?.promocode !== ''}
            selectedPlanType={selectedPlanType}
            isArgentina={isArgentina}
            isFreeAccount={isFreeAccount}
            disabledPromocode={disabledPromocode}
            handleRemovePromocodeApplied={removePromocodeApplied}
            currentPromocodeApplied={promocodeApplied}
            defaultPromocodeDismissed={defaultPromocodeDismissed}
            handleManualPromocodeIntervention={handleManualPromocodeIntervention}
            registerClearPromocodeInput={registerClearPromocodeInput}
          />
        )}
        <section>
          <h4>{_('buy_process.subscriptions_title')}</h4>
          {items.map((item, index) => (
            <ItemCart key={`item-${index}`} {...item} />
          ))}
          <hr className="m-t-24 m-b-24" />
          <h3 className="dp-total-purchase">
            {isLandingPagesDowngrade ? (
              <>
                Total
                <span>
                  US$ <FormattedNumber value={0} {...numberFormatOptions} />
                  {isTransfer ? '*' : ''}
                </span>
              </>
            ) : (
              <>
                Total
                <span>
                  <>
                    {isArgentinaTransfer(billingCountry, selectedPaymentMethod) ? (
                      <>
                        {'$ '}
                        <FormattedNumber
                          value={(total + taxes) * currencyRate}
                          {...numberFormatOptions}
                        />
                        {'**'}
                      </>
                    ) : (
                      <>
                        {'US$ '}
                        <FormattedNumber value={total} {...numberFormatOptions} />
                        {'*'}
                      </>
                    )}
                  </>
                </span>
              </>
            )}
          </h3>
          {buyButton}
          {checkoutLandingPackButtonEnabled && (
            <p className="dp-reminder">
              {_('landing_selection.user_messages.legend_after_remove')}
            </p>
          )}
        </section>
        {(!isByCredits ||
          (isByCredits && buyType === BUY_LANDING_PACK && amountDetailsLandingPacksData !== null) ||
          (isByCredits && buyType === BUY_CHAT_PLAN && amountDetailsAddOnPlanData !== null) ||
          (isByCredits && buyType === BUY_ONSITE_PLAN && amountDetailsAddOnPlanData !== null) ||
          (isByCredits &&
            buyType === BUY_PUSH_NOTIFICATION_PLAN &&
            amountDetailsAddOnPlanData !== null)) &&
          (amountDetailsData?.value?.nextMonthDate ||
            amountDetailsLandingPacksData?.value?.nextMonthDate) && (
            <NextInvoices
              pathname={pathname}
              search={search}
              billingCountry={billingCountry}
              selectedPaymentMethod={selectedPaymentMethod}
              nextMonthDate={
                amountDetailsData?.value?.nextMonthDate ??
                amountDetailsLandingPacksData?.value?.nextMonthDate
              }
              nextMonthTotal={nextMonthTotal}
              currencyRate={currencyRate}
              nextMonthTaxes={nextMonthTaxes}
              subtitleBuyId={
                buyType === BUY_LANDING_PACK
                  ? 'buy_process.upcoming_bills.landing_pack_subtitle'
                  : buyType === BUY_ONSITE_PLAN
                    ? 'buy_process.upcoming_bills.onsite_plan_subtitle'
                    : buyType === BUY_PUSH_NOTIFICATION_PLAN
                      ? 'buy_process.upcoming_bills.push_notification_plan_subtitle'
                      : buyType === BUY_CHAT_PLAN
                        ? 'buy_process.upcoming_bills.chat_plan_subtitle'
                        : buyType === BUY_ECO_IA_PLAN
                          ? 'buy_process.upcoming_bills.ecoia_plan_subtitle'
                          : 'buy_process.upcoming_bills.marketing_plan_subtitle'
              }
            />
          )}
        {items.map((item, index) => (
          <footer key={`footer-${index}`}>
            <ul>
              {item.subscriptionItems.map((subscriptionItem, index) => (
                <li key={`subscription-item-${index}`}>
                  <p>{subscriptionItem}</p>
                </li>
              ))}
            </ul>
          </footer>
        ))}

        {/* {subscriptionItems?.length > 0 && (
                <>
                  <hr />
                  <div className="dp-subscription-items">
                    <ul>
                      {subscriptionItems.map((subscriptionItem, index) => (
                        <li key={`subscription-item-${index}`}>
                          <p>{subscriptionItem}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )} */}
      </div>
    );
  },
);

ShoppingCart.propTypes = {
  selectedMarketingPlan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.PropTypes.string,
  }),
  isEqualPlan: PropTypes.bool,
  canBuy: PropTypes.bool,
  selectedPaymentMethod: PropTypes.string,
};
